import { addHours, format, set, subDays } from 'date-fns';

import { PlannedCareFormValues } from 'src/hooks/forms/useResidentCareTaskForm';
import {
    CareTask,
    CareTaskChartingReason,
    CareTaskChartRequirement,
    CareTaskField,
    CareTaskRequirement,
    CareTaskSupportedCustomOutcome,
    OutcomeType,
    ResidentActivityUpdatePlannedCareTask,
    ResolveTaskUpdatePlannedCareTask,
    UpdatePlannedCareTask,
} from 'src/store/sageAdminApi';

/*
The planned care form is dynamic and based on the backend configuration for each care task. 
The form fields are determined by the care task's chart requirements and the user's selection of complete vs incomplete

The logic in this block is responsible for determining how each individal planned care form appears
*/
export type ChartType = 'complete' | 'incomplete';

interface FormFieldRequirement {
    isShown: boolean;
    isRequired: boolean;
}

export type PlannedCareFormFields = {
    showIncompleteReasonButtons?: boolean;
    supportedChartingReasons?: CareTaskChartingReason[];
    supportedCustomOutcomes?: CareTaskSupportedCustomOutcome[];
    // TODO: Would be nice if the FormFieldRequirement fields were grouped
    startTimeField: FormFieldRequirement;
    endTimeField: FormFieldRequirement;
    durationField: FormFieldRequirement;
    noteField: FormFieldRequirement;
    reasonField: FormFieldRequirement;
};

const AllCareTaskFields: CareTaskField[] = ['START_TIME', 'END_TIME', 'DURATION', 'NOTE', 'REASON'];
const CareTaskTimeFields: CareTaskField[] = ['START_TIME', 'END_TIME', 'DURATION'];
const CareTaskNonTimeFields: CareTaskField[] = AllCareTaskFields.filter(
    (field) => !CareTaskTimeFields.includes(field)
);

const IncompleteOutcomes: OutcomeType[] = ['NOT_COMPLETED', 'REFUSED'];

export const getDefaultOutcomeForChartType = (chartType: ChartType): OutcomeType => {
    if (chartType === 'incomplete') {
        return 'NOT_COMPLETED';
    }

    return 'COMPLETED';
};

export const getChartingReasonsForChartType = (
    careTask: CareTask,
    chartType: ChartType
): CareTaskChartingReason[] => {
    const supportedChartingReasons = careTask.supportedChartingReasons ?? [];

    return supportedChartingReasons.filter((reason) => {
        if (!reason.relatedOutcome) {
            return true;
        }

        if (chartType === 'incomplete') {
            return IncompleteOutcomes.includes(reason.relatedOutcome);
        }

        return reason.relatedOutcome === 'COMPLETED';
    });
};

export const getOutcomeForChartingReason = (
    reason: CareTaskChartingReason | undefined,
    chartType: ChartType
): OutcomeType => {
    if (reason?.relatedOutcome) {
        return reason.relatedOutcome;
    }

    return getDefaultOutcomeForChartType(chartType);
};

function getFieldRequirements(chartType: ChartType, requirement: CareTaskRequirement) {
    const isRequired =
        requirement === 'REQUIRED' ||
        (chartType === 'incomplete' && requirement === 'REQUIRED_INCOMPLETE') ||
        (chartType === 'complete' && requirement === 'REQUIRED_COMPLETE');

    if (isRequired) {
        return { isShown: true, isRequired: true };
    }

    const isShown =
        requirement === 'OPTIONAL' ||
        (chartType === 'incomplete' && requirement === 'OPTIONAL_INCOMPLETE') ||
        (chartType === 'complete' && requirement === 'OPTIONAL_COMPLETE');

    return { isShown, isRequired: false };
}

function getPlannedCareFieldRequirements(
    chartType: ChartType,
    careTaskChartRequirements: CareTaskChartRequirement[] = [],
    fields: CareTaskField[]
): Record<CareTaskField, FormFieldRequirement> {
    const requirementsByField = careTaskChartRequirements.reduce((acc, chartRequirement) => {
        const existing = acc[chartRequirement.field];
        const incoming = getFieldRequirements(chartType, chartRequirement.requirement);
        return {
            ...acc,
            [chartRequirement.field]: {
                isShown: existing?.isShown || incoming.isShown,
                isRequired: existing?.isRequired || incoming.isRequired,
            },
        };
    }, {} as Record<CareTaskField, FormFieldRequirement>);

    return fields.reduce(
        (acc, field) => ({
            ...acc,
            [field]: requirementsByField[field] || { isShown: false, isRequired: false },
        }),
        {} as Record<CareTaskField, FormFieldRequirement>
    );
}

export const buildPlannedCareFormFields = (
    careTask: CareTask,
    chartType: ChartType,
    restrictTimeFields: boolean = false
): PlannedCareFormFields => {
    // Incomplete selection is based off of completion status and always required
    const showIncompleteReasonButtons = chartType === 'incomplete';
    const { supportedCustomOutcomes, chartRequirements } = careTask;
    const supportedChartingReasons = getChartingReasonsForChartType(careTask, chartType);

    if (restrictTimeFields) {
        const chartRequirementsWithoutTimeFields = getPlannedCareFieldRequirements(
            chartType,
            chartRequirements,
            CareTaskNonTimeFields
        );

        return {
            showIncompleteReasonButtons,
            supportedChartingReasons,
            supportedCustomOutcomes,
            startTimeField: { isShown: false, isRequired: false },
            endTimeField: { isShown: false, isRequired: false },
            durationField: { isShown: false, isRequired: false },
            noteField: chartRequirementsWithoutTimeFields['NOTE'],
            reasonField: chartRequirementsWithoutTimeFields['REASON'],
        };
    }

    const allChartRequirementsByField = getPlannedCareFieldRequirements(
        chartType,
        chartRequirements,
        AllCareTaskFields
    );

    return {
        showIncompleteReasonButtons,
        supportedChartingReasons,
        supportedCustomOutcomes,
        startTimeField: allChartRequirementsByField['START_TIME'],
        endTimeField: allChartRequirementsByField['END_TIME'],
        durationField: allChartRequirementsByField['DURATION'],
        noteField: allChartRequirementsByField['NOTE'],
        reasonField: allChartRequirementsByField['REASON'],
    } as PlannedCareFormFields;
};

export const generateDefaultFormValues = (
    careTask: CareTask,
    chartType: ChartType,
    shouldRestrictTimeFields: boolean = false
) => {
    const { durationField, startTimeField, endTimeField } = buildPlannedCareFormFields(
        careTask,
        chartType,
        shouldRestrictTimeFields
    );

    const defaultDurationInMinutes =
        careTask.estimatedTimeMs && durationField.isShown
            ? Math.ceil(careTask.estimatedTimeMs / 60000)
            : undefined;
    const defaultStartTime =
        careTask.scheduledAtMs && startTimeField.isShown
            ? format(careTask.scheduledAtMs, 'HH:mm')
            : undefined;
    const defaultEndTime =
        careTask.estimatedTimeMs && careTask.scheduledAtMs && endTimeField.isShown
            ? format(careTask.scheduledAtMs + careTask.estimatedTimeMs, 'HH:mm')
            : undefined;

    return { defaultDurationInMinutes, defaultStartTime, defaultEndTime };
};

export const buildUpdatePlannedCareTaskRequest = (
    careTaskForm: PlannedCareFormValues,
    staffRid: string
): {
    residentActivityRequest: ResidentActivityUpdatePlannedCareTask;
    resolveTaskRequest: ResolveTaskUpdatePlannedCareTask;
} => {
    const { startTimeMs, endTimeMs } = convertStartAndEndTimeToMs(
        careTaskForm.startTime,
        careTaskForm.endTime
    );

    const chartedOutcome =
        careTaskForm.outcome ?? getDefaultOutcomeForChartType(careTaskForm.chartType);

    const sharedValues = {
        id: careTaskForm.careTaskRid,
        // We don't want to send the backend an empty string
        note: careTaskForm.note || undefined,
        chartedOutcome,
        chartedCustomOutcomeId: careTaskForm.chartedCustomOutcomeId,
        chartedReasonId: careTaskForm.chartedReasonId,
        chartedAtMs: Date.now(),
        respondingStaffMemberIds: [staffRid],
    } as UpdatePlannedCareTask;

    const residentActivityRequest = {
        ...sharedValues,
        startTimeMs: startTimeMs,
        endTimeMs: endTimeMs,
        durationMs: careTaskForm.durationInMinutes
            ? careTaskForm.durationInMinutes * 60 * 1000
            : undefined,
    } as ResidentActivityUpdatePlannedCareTask;

    const resolveTaskRequest = {
        ...sharedValues,
    } as ResolveTaskUpdatePlannedCareTask;

    return { residentActivityRequest, resolveTaskRequest };
};

// React hook form doesn't support dots in field names
export const createFormIdFromCareTask = (careTask: CareTask) => careTask.id.replace(/\./g, '_');

/*
Converts a start and end times in format HH:mm to milliseconds. These can stretch overnight
This is inherently ambigious without a date (which we don't have for UX reasons)
But, we can make some assumptions based on the current time to get a strong guess
1) End time at most an hour after the current time <-- add an hour just to buffer
2) Start time is ALWAYS before the end time

It's technically possible we only have access to start time and not end time
In this case we can just use the current time (+1 hour) as the "end time" and apply the same logic

Examples:
Current time: 2am
Start time: 9pm, End time: 1am => Start time is yesterday at 9pm, end time is today at 1am
Start time: 1am, End time: 2:15am => Start time is today at 1am, end time is today at 2:15am
Start time: 8pm, End time: 11pm => Start time is yesterday at 8pm, end time is yesterday at 11pm
Start time: 8pm, End time: 4am => Start time is two days ago at 8pm, end time is yesterday at 4am <- unexpected case
Start time: 8pm, End time: undefined => Start time is yesterday at 8pm, end time is undefined
*/
export const convertStartAndEndTimeToMs = (startTime?: string, endTime?: string) => {
    if (!startTime && !endTime) return {};

    const maxEndTimeMs = addHours(new Date(), 1).getTime();

    // Finds the most recent time that's before upperBoundMs
    const convertToMsWithUpperbound = (time: string, upperBoundMs: number): number => {
        const [hours, minutes] = time.split(':').map(Number);
        const upperBoundDate = new Date(upperBoundMs);

        let timeOnUpperBoundDateDay = set(upperBoundDate, {
            hours,
            minutes,
            seconds: 0,
            milliseconds: 0,
        });

        // If this time is after the upper bound, go back one day
        if (timeOnUpperBoundDateDay.getTime() > upperBoundMs) {
            timeOnUpperBoundDateDay = subDays(timeOnUpperBoundDateDay, 1);
        }

        return timeOnUpperBoundDateDay.getTime();
    };

    const endTimeMs = endTime ? convertToMsWithUpperbound(endTime, maxEndTimeMs) : undefined;
    const startTimeMs = startTime
        ? convertToMsWithUpperbound(startTime, endTimeMs ?? maxEndTimeMs)
        : undefined;

    return { startTimeMs, endTimeMs };
};
